import { VenueTableQr, VenueTableQrSection, VenueTableQrTable } from './types/api'
import { resolveI18n } from './i18n'

// ограничения: список столов уходит в данные страницы меню каждому гостю — держим его небольшим
export const TABLE_QR_LIMITS = { tables: 300, sections: 30, removedSections: 10 }

// Общая логика «QR-кодов столов» для витрины и бэкенда.

// стол по номеру и залу из QR
export function findTableQrTable(
  tableQr: VenueTableQr | undefined,
  number: string | undefined,
  sectionId?: string | null,
): VenueTableQrTable | undefined {
  if (!tableQr || !number) {
    return undefined
  }
  const sameNumber = (tableQr.tables ?? []).filter((table) => {
    return table.number === number
  })
  if (sectionId) {
    return sameNumber.find((table) => {
      return table.sectionId === sectionId
    })
  }
  // QR стола без зала — ссылка без зала; иначе номер должен быть однозначен по заведению
  const withoutSection = sameNumber.find((table) => {
    return !table.sectionId
  })
  if (withoutSection) {
    return withoutSection
  }
  return sameNumber.length === 1 ? sameNumber[0] : undefined
}

export function findTableQrSection(
  tableQr: VenueTableQr | undefined,
  sectionId: string | undefined,
): VenueTableQrSection | undefined {
  if (!sectionId) {
    return undefined
  }
  return (tableQr?.sections ?? []).find((section) => {
    return section.id === sectionId
  })
}

// название зала на языке гостя (перевод заведения), иначе — основное
export function tableQrSectionName(
  section: VenueTableQrSection | undefined,
  language?: string,
): string | undefined {
  if (!section) {
    return undefined
  }
  return (
    (language ? resolveI18n(section.nameI18n, section.name, language) : section.name) || undefined
  )
}

// название зала для гостя и персонала — только если залов несколько (один зал = просто «#4»)
export function visibleTableQrSectionName(
  tableQr: VenueTableQr | undefined,
  sectionId: string | undefined,
  language?: string,
): string | undefined {
  if ((tableQr?.sections ?? []).length < 2) {
    return undefined
  }
  return tableQrSectionName(findTableQrSection(tableQr, sectionId), language)
}

// «Веранда #4» — без слова «стол»: бывают и столы, и бильярд, и кабинки
export function formatTableQrTable(
  tableQr: VenueTableQr | undefined,
  table: VenueTableQrTable,
  language?: string,
) {
  const sectionName = visibleTableQrSectionName(tableQr, table.sectionId, language)
  return [sectionName, `#${table.number}`].filter(Boolean).join(' ')
}

// пожелание по месту при брони: «Веранда #4», «Веранда», «#4» или undefined (не указано / нет такого)
export function formatTableQrWish(
  tableQr: VenueTableQr | undefined,
  sectionId: string | undefined,
  number: string | undefined,
  language?: string,
): string | undefined {
  if (!tableQr?.enabled) {
    return undefined
  }
  const table = number ? findTableQrTable(tableQr, number, sectionId) : undefined
  const section = findTableQrSection(tableQr, table?.sectionId ?? sectionId)
  if (number && !table) {
    return undefined
  }
  const sectionName =
    (tableQr.sections ?? []).length > 1 || !table
      ? tableQrSectionName(section, language)
      : undefined
  const text = [sectionName, table ? `#${table.number}` : undefined].filter(Boolean).join(' ')
  return text || undefined
}

// подпись под напечатанным QR-кодом: «Веранда #4» или «#4» — по тумблеру заведения
export function formatTableQrCaption(tableQr: VenueTableQr | undefined, table: VenueTableQrTable) {
  const sectionName =
    tableQr?.qrShowSection !== false
      ? findTableQrSection(tableQr, table.sectionId)?.name
      : undefined
  return [sectionName, `#${table.number}`].filter(Boolean).join(' ')
}

// ---------- действия панели над залами (общие для панели и проверок) ----------

// удалить зал: столы уходят вместе с ним, а зал — в архив (его можно вернуть с тем же id)
export function removeTableQrSection(
  tableQr: VenueTableQr,
  sectionId: string,
  now: string,
): VenueTableQr {
  const section = (tableQr.sections ?? []).find((candidate) => {
    return candidate.id === sectionId
  })
  if (!section) {
    return tableQr
  }
  return {
    ...tableQr,
    sections: (tableQr.sections ?? []).filter((candidate) => {
      return candidate.id !== sectionId
    }),
    tables: (tableQr.tables ?? []).filter((table) => {
      return table.sectionId !== sectionId
    }),
    removedSections: [
      ...(tableQr.removedSections ?? []).filter((removed) => {
        return removed.id !== sectionId
      }),
      {
        id: section.id,
        name: section.name.trim(),
        removedAt: now,
        tables: (tableQr.tables ?? [])
          .filter((table) => {
            return table.sectionId === sectionId && table.number.trim() !== ''
          })
          .map((table) => {
            return table.number.trim()
          }),
      },
    ],
  }
}

// вернуть удалённый в панели зал: тот же id и те же номера — старые QR снова работают
export function restoreTableQrSection(tableQr: VenueTableQr, removedId: string): VenueTableQr {
  const removed = (tableQr.removedSections ?? []).find((candidate) => {
    return candidate.id === removedId
  })
  if (
    !removed ||
    (tableQr.sections ?? []).some((section) => {
      return section.id === removedId
    })
  ) {
    return tableQr
  }
  return {
    ...tableQr,
    sections: [...(tableQr.sections ?? []), { id: removed.id, name: removed.name }],
    tables: [
      ...(tableQr.tables ?? []),
      ...removed.tables.map((number) => {
        return { number, sectionId: removed.id }
      }),
    ],
    removedSections: (tableQr.removedSections ?? []).filter((candidate) => {
      return candidate.id !== removedId
    }),
  }
}

// зал пересоздали в кассе: новому залу отдаём id старого — QR старого зала ведут на новый
// (номер стола в QR должен совпасть с номером стола нового зала)
export function rebindTableQrSection(
  tableQr: VenueTableQr,
  removedId: string,
  targetId: string,
): VenueTableQr {
  const removedExists = (tableQr.removedSections ?? []).some((candidate) => {
    return candidate.id === removedId
  })
  const idTaken = (tableQr.sections ?? []).some((section) => {
    return section.id === removedId
  })
  if (!removedExists || idTaken || removedId === targetId) {
    return tableQr
  }
  return {
    ...tableQr,
    sections: (tableQr.sections ?? []).map((section) => {
      return section.id === targetId ? { ...section, id: removedId } : section
    }),
    tables: (tableQr.tables ?? []).map((table) => {
      return table.sectionId === targetId ? { ...table, sectionId: removedId } : table
    }),
    removedSections: (tableQr.removedSections ?? []).filter((candidate) => {
      return candidate.id !== removedId
    }),
  }
}
